/** @jsx jsx */
import { Grid } from "@cd2/components/build/es6"
import { css, jsx } from "@emotion/core"
import { useState } from "react"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { HoverOrderLoader } from "../hoverLoaders/HoverOrderLoader"
import { IRecord } from "../types/schema"
import { QuickToolTip } from "./QuickTooltip"

export function ConsolidatedOrderList({
  consolidatedOrderKey,
  refetchKey,
}: {
  consolidatedOrderKey?: string
  refetchKey?: any
}) {
  const orders = useConsolidatedOrders(consolidatedOrderKey, { refetchOn: refetchKey })
  return <ConsolidatedOrderListComponent orders={orders} />
}

export function ConsolidatedOrderListComponent({
  orders,
}: {
  orders: IRecord<"orders", "identifier">[] | null
}) {
  if (!orders || orders.length === 0) {
    return null
  }
  const displayed = orders.slice(0, 2)
  const hasMore = orders.length > displayed.length
  return (
    <div>
      {displayed.map((order, index) => (
        <span key={order.id}>
          <HoverOrderLoader id={order.id} initialData={order as any} />
          {index < displayed.length - 1 ? ", " : " "}
        </span>
      ))}
      {hasMore && (
        <QuickToolTip
          interactive={true}
          color="white"
          content={
            <Grid gap={8}>
              {orders
                .filter(v => !displayed.find(x => x.id === v.id))
                .map((order: any) => (
                  <HoverOrderLoader key={order.id} id={order.id} initialData={order} />
                ))}
            </Grid>
          }
        >
          <span
            css={css`
              border-bottom: 1px dotted grey;
              /* padding-left: 4px; */
            `}
          >
            + {orders.length - displayed.length}
          </span>
        </QuickToolTip>
      )}
    </div>
  )
}

export function useConsolidatedOrders(
  key?: string,
  opts?: { refetchOn?: any; readyToExport?: boolean },
) {
  const skeem = window.skeem
  const [orders, setOrders] = useState<IRecord<"orders", "identifier">[] | null>(null)

  async function loadOrders() {
    if (!key || key === "will-consolidate") {
      setOrders(null)
      return
    }
    const readyToExportFilter = opts?.readyToExport
      ? { matches: { sage_order_number: "" } }
      : undefined
    const orders = await skeem.fetch2<IRecord<"orders", "identifier">[]>({
      model: {
        name: "orders",
        attributes: ["identifier"],
        filter: {
          and: [
            { matches: { consolidated_orders_key: key } },
            readyToExportFilter,
            {
              // exclude orders that have a BOM item
              eq: [
                {
                  count: {
                    path: [{ name: "items", filter: { matches: { type: { or: [3, 4] } } } }],
                  },
                },
                { value: 0 },
              ],
            },
          ].filter(Boolean),
        },
        sort: { by: "identifier", direction: "asc" },
      },
    })
    setOrders(orders)
  }

  useAsyncEffect(async () => {
    await loadOrders()
  }, [key, opts?.refetchOn])

  return orders
}
